import { Menu, MenuItem, useTheme } from "@mui/material"
import { useSnackbarContext } from "../services/snackbar"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { PlaylistType } from "../services/api/playlist"
import { useNavigate } from "react-router-dom"
import { useStaticContext } from "../services/static"
import { httpClient } from "../services/http"

interface PlaylistContextMenuProps {
  playlist: PlaylistType
  onClose?: () => void
  open: boolean
  position: { x: number; y: number } | null
}

export const PlaylistContextMenu = ({
  playlist,
  onClose,
  open,
  position,
}: PlaylistContextMenuProps): React.ReactElement => {
  const { open: sbOpen } = useSnackbarContext()
  const { role, userId } = useAuthContext()
  const navigate = useNavigate()
  const theme = useTheme()
  const { playlists, reloadPlaylists } = useStaticContext()

  const saved = playlists.some((pl) => pl.code === playlist.code)

  const handleSave = async () => {
    if (!playlist) return
    try {
      if (saved) {
        await httpClient.instance.delete(`/library/${playlist.code}`)
        sbOpen("Playlist supprimée de votre bibliothèque", "info")
      } else {
        await httpClient.instance.put(`/library/${playlist.code}`)
        sbOpen("Playlist ajoutée à votre bibliothèque", "success")
      }
    } catch (e) {
      sbOpen("Une erreur s'est produite", "error")
      console.error(e)
    } finally {
      reloadPlaylists()
    }
  }

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position !== null ? { top: position.y, left: position.x } : undefined}
      slotProps={{
        paper: {
          sx: {
            overflow: "hidden",
            backgroundColor: theme.palette.element.contextMenu?.background ?? theme.palette.primary.main,
            backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
          },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          navigator.clipboard.writeText(`${window.location.origin}/playlist?pl=${playlist.code}`)
          sbOpen("Lien copié !", "success")
          onClose?.()
        }}
      >
        {"Copier le lien"}
      </MenuItem>
      {(playlist.authorId === userId || role === UserRole.GEEK) && (
        <MenuItem
          onClick={() => {
            navigate(`/editPlaylist/${playlist.code}`)
            onClose?.()
          }}
        >
          {"Modifier"}
        </MenuItem>
      )}
      {playlist.authorId !== userId && (
        <MenuItem
          onClick={() => {
            handleSave()
            onClose?.()
          }}
        >
          {saved ? "Retirer de ma bibliothèque" : "Ajouter à ma bibliothèque"}
        </MenuItem>
      )}
    </Menu>
  )
}
